"use client"

import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react"
import useToggleState from "@lib/hooks/use-toggle-state"
import { ArrowRightMini, XMark } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { clx } from "@modules/common/components/ui"
import { Fragment } from "react"
import CountrySelect from "../country-select"
import LanguageSelect from "../language-select"
import { Locale } from "@lib/data/locales"

const HelpItems = {
  "Tu cuenta": "/account",
  "Tus pedidos": "/account/orders",
  "Tus direcciones": "/account/addresses",
  "Cesta": "/cart",
}

type SideMenuProps = {
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
  categories: { name: string; handle: string }[]
}

const SideMenu = ({ regions, locales, currentLocale, categories }: SideMenuProps) => {
  const countryToggleState = useToggleState()
  const languageToggleState = useToggleState()

  return (
    <Popover className="flex">
      {({ open, close }) => (
        <>
          <PopoverButton
            data-testid="nav-menu-button"
            className="amz-nav-item flex items-center gap-x-1 font-bold focus:outline-none"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" stroke="white" strokeWidth="2.2" aria-hidden>
              <path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" />
            </svg>
            Todo
          </PopoverButton>

          {open && (
            <div
              className="fixed inset-0 z-[60] bg-black/70"
              onClick={close}
              data-testid="side-menu-backdrop"
            />
          )}

          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <PopoverPanel
              static
              className="fixed left-0 top-0 bottom-0 z-[61] w-[85vw] max-w-[365px] bg-white text-[#0f1111] overflow-y-auto whitespace-normal"
              data-testid="nav-menu-popup"
            >
              <div className="bg-amz-navy-light text-white px-8 py-3 flex items-center justify-between">
                <LocalizedClientLink href="/account" onClick={close} className="text-lg font-bold">
                  Hola, identificate
                </LocalizedClientLink>
                <button data-testid="close-menu-button" onClick={close} aria-label="Cerrar menu">
                  <XMark />
                </button>
              </div>

              <div className="py-2">
                <h3 className="px-8 pt-3 pb-2 text-lg font-bold">Comprar por departamento</h3>
                <ul>
                  {categories.map((c) => (
                    <li key={c.handle}>
                      <LocalizedClientLink
                        href={`/categories/${c.handle}`}
                        onClick={close}
                        className="flex items-center justify-between px-8 py-3 text-sm hover:bg-[#eaeded]"
                      >
                        {c.name}
                        <ArrowRightMini />
                      </LocalizedClientLink>
                    </li>
                  ))}
                  <li>
                    <LocalizedClientLink
                      href="/store"
                      onClick={close}
                      className="flex items-center justify-between px-8 py-3 text-sm hover:bg-[#eaeded]"
                    >
                      Ver todo
                      <ArrowRightMini />
                    </LocalizedClientLink>
                  </li>
                </ul>
              </div>

              <div className="border-t border-[#d5dbdb] py-2">
                <h3 className="px-8 pt-3 pb-2 text-lg font-bold">Ayuda y configuracion</h3>
                <ul>
                  {Object.entries(HelpItems).map(([name, href]) => (
                    <li key={name}>
                      <LocalizedClientLink
                        href={href}
                        onClick={close}
                        className="block px-8 py-3 text-sm hover:bg-[#eaeded]"
                      >
                        {name}
                      </LocalizedClientLink>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col gap-y-4 px-8 py-3 text-sm">
                  {!!locales?.length && (
                    <div
                      className="flex justify-between"
                      onMouseEnter={languageToggleState.open}
                      onMouseLeave={languageToggleState.close}
                    >
                      <LanguageSelect
                        toggleState={languageToggleState}
                        locales={locales}
                        currentLocale={currentLocale}
                      />
                      <ArrowRightMini
                        className={clx(
                          "transition-transform duration-150",
                          languageToggleState.state ? "-rotate-90" : ""
                        )}
                      />
                    </div>
                  )}
                  <div
                    className="flex justify-between"
                    onMouseEnter={countryToggleState.open}
                    onMouseLeave={countryToggleState.close}
                  >
                    {regions && (
                      <CountrySelect toggleState={countryToggleState} regions={regions} />
                    )}
                    <ArrowRightMini
                      className={clx(
                        "transition-transform duration-150",
                        countryToggleState.state ? "-rotate-90" : ""
                      )}
                    />
                  </div>
                </div>
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  )
}

export default SideMenu
